import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
	Form, ControlledSubmitButton, Textarea, Label
} from 'components/Form';
import { Modal, OverlayContent, useOverlayState } from 'components/Overlay';
import { Button } from 'components/Button';
import UserCollectionForm from 'features/productReviews/components/UserCollectionForm';
import { uniqueId } from 'utilities/dom';
import { utagLinkPDP } from 'features/tealium';

import { addAnswer, registerUserEmail } from '../slice';
import { MAX_CHAR } from '../config';

import './ProductSubmitAnswerForm.scss';

import useCharOverAndAvailCount from './useCharOverAndAvailCount';

const ProductSubmitAnswerForm = ({
	questionId,
	onCancel,
	handleShowThankYouMessage,
	pageNumber,
	questionPos,
	fromSearch = false
}) => {

	const charOverAndAvailCount = useCharOverAndAvailCount();

	const [ disabledSubmit, setDisabledSubmit ] = useState(true);
	const [ answer, setAnswer ] = useState({});
	const createThankYouAnswerOverlay = useOverlayState(false);
	const [ textAreaValue, setTextAreaValue ] = useState('');

	const dispatch = useDispatch();
	const answerInputId = uniqueId();

	const trackCharacterOver = (overCount) => {

		if (fromSearch || overCount === 0) return;

		const charOverMessage = overCount === 1 ? '1 character over' : `${overCount} characters over`;

		utagLinkPDP('TurnTo')({
			eventAction: `Q&A-AnswerError-${charOverMessage}`,
			eventLabel: `${pageNumber}-${questionPos}`
		});

	};

	const handleAnswerInputChange = (e) => {

		const input = e.target.value;
		setTextAreaValue(input);

		// if input value is blank and only has spaces, disable submit button
		if (input.trim() === '') {

			charOverAndAvailCount.setCountWhenInputBlank();
			setDisabledSubmit(true);

		} else if (input.length > MAX_CHAR) {

			charOverAndAvailCount.setCountWhenInputOverMax(input);
			setDisabledSubmit(true);
			trackCharacterOver(input.length - MAX_CHAR);

		} else {

			const availCount = MAX_CHAR - input.length;

			charOverAndAvailCount.setCountWhenInputLessThanMax(availCount);
			setDisabledSubmit(availCount === MAX_CHAR);

		}

	};

	const handleSubmit = (data) => {

		const formData = { ...data };
		// Submit prop is not necessary for submission of the form
		if (typeof formData.Submit === 'undefined') {

			delete formData.Submit;

		}

		setAnswer(formData[answerInputId].trim());

		createThankYouAnswerOverlay.showOverlay();

		if (!fromSearch && pageNumber && questionPos) {

			utagLinkPDP('TurnTo')({
				eventAction: 'Q&A-answerSubmit',
				eventLabel: `${pageNumber}-${questionPos}`
			});

		}

	};

	async function proccessSubmitAnswer (collectionData) {

		// register user
		dispatch(registerUserEmail({ ...collectionData }));

		// prepare answer payload for submission to API
		const answerPayload = {
			questionId,
			text: answer,
			user: {
				firstName: collectionData.firstName,
				lastName: collectionData.lastName,
				emailAddress: collectionData.emailAddress
			}
		};

		dispatch(addAnswer(answerPayload));
		onCancel();
		createThankYouAnswerOverlay.hideOverlay();
		handleShowThankYouMessage();

	}

	return (
		<div className="ProductSubmitAnswerForm">
			<Form onSubmit={handleSubmit}>
				<Label htmlFor={answerInputId} visuallyHidden>
					Enter an answer to this question
				</Label>
				<Textarea
					id={answerInputId}
					name={answerInputId}
					value={textAreaValue}
					rows="5"
					placeholder="Enter an answer to this question"
					onChange={handleAnswerInputChange}
				/>
				{charOverAndAvailCount.availCharCount <= 200 && charOverAndAvailCount.charOver === 0 && (
					<span className="ProductSubmitAnswerForm__charactersLeft">
						{charOverAndAvailCount.availCharCount}
						{' '}
						{charOverAndAvailCount.availCharCount === 1 ? 'character left' : 'characters left'}
					</span>
				)}
				{charOverAndAvailCount.charOver > 0 && (
					<span className="ProductSubmitAnswerForm__charactersOver">
						{charOverAndAvailCount.charOver}
						{' '}
						{charOverAndAvailCount.charOver === 1 ? 'character over' : 'characters over'}
					</span>
				)}
				<div className="ProductSubmitAnswerForm__disclaimerWrapper">
					<span className="ProductSubmitAnswerForm__disclaimerIcon lpIcon-alert" />
					<span className="ProductSubmitAnswerForm__disclaimerText">
						Do not include HTML, links, references to other stores, pricing or contact info.
					</span>
				</div>
				<div className="ProductSubmitAnswerForm__btnWrapper">
					<ControlledSubmitButton name="Submit" disabled={disabledSubmit} buttonStyleVariant="secondary" />
					<Button buttonStyleVariant="white" onClick={() => onCancel()}>
						Cancel
					</Button>
				</div>
			</Form>
			<Modal {...createThankYouAnswerOverlay} aria-label="Thank You For Answering" escapeExits>
				<OverlayContent>
					<div className="ProductSubmitAnswerForm__submitAnswerModal">
						<UserCollectionForm
							title="Thank You For Answering"
							topCopy="Please verify your information so we can post your answer."
							bottomCopy="If you uncheck this option, you may still receive emails related to your answer."
							source="ProductAnswer"
							onCancel={() => createThankYouAnswerOverlay.hideOverlay()}
							onSubmit={proccessSubmitAnswer}
						/>
					</div>
				</OverlayContent>
			</Modal>
		</div>
	);

};

ProductSubmitAnswerForm.propTypes = {
	questionId: PropTypes.oneOfType([ PropTypes.number, PropTypes.string ]),
	onCancel: PropTypes.func,
	handleShowThankYouMessage: PropTypes.func,
	fromSearch: PropTypes.bool,
	pageNumber: PropTypes.number,
	questionPos: PropTypes.number
};

export default ProductSubmitAnswerForm;
